/**
 * "Add a lot at the ring" - the walk-in lot dialog (C2b).
 *
 * It replaces a chain of seven browser prompt() boxes plus two confirm() boxes
 * on the clerk console. A native box stops the page while it is open (the ring
 * keeps talking to a console that cannot repaint), shows no field next to its
 * label, and cannot be backed out of one question at a time. This is one sheet,
 * in the console's own sheet style (.scrim / .sheetcard, the same family as
 * Reset bidding and Increment): number, name, opening ask, reserve, kind, how
 * it sells, and the seller. Enter moves from box to box and adds from the last
 * one; Escape leaves.
 *
 * The dialog owns the form and nothing else. Validation is floor-ops's
 * validateNewLot (shared with Horse Check-In), the seller search is
 * seller-search.ts (shared with Horse Check-In), and the API call belongs to
 * the caller: submit() sends the lot and says what happened.
 *
 * Built with el()/setChildren() only. Seller names come from the API, and the
 * console is a credentialed origin: nothing here goes through innerHTML.
 */

import { el, setChildren } from './dom.js';
import { nextLotNumber, parseDollars, validateNewLot, type NewLotResult } from './floor-ops.js';
import type { LotWords } from './lot-term.js';
import { searchSellers, type Seller } from './seller-search.js';

/** What the dialog hands to submit(): the createLot body, before the caller adds the ring. */
export type NewLotBody = Extract<NewLotResult, { ok: true }>['body'];

/**
 * `unconfirmed` marks a failure that may have landed after the write (no answer,
 * a server error): the dialog remembers that number, so a later "taken" answer
 * for it is read as the clerk's own lot even if the name was edited meanwhile.
 */
export type SubmitResult = { ok: true } | { ok: false; error: string; unconfirmed?: boolean };

export interface LotDialogDeps {
  words: LotWords;
  kinds: readonly string[];
  /** The kind the form starts on (the one used for the last lot added). */
  kind: string;
  /**
   * Every lot of the sale. Read when the dialog opens, and again on each Add, so
   * a number another clerk took meanwhile is refused. The title lets the dialog
   * tell "this number is taken" from "this is the lot I just added" (a reply
   * that was lost on a slow link).
   */
  existing: () => readonly { hipNumber: string; runOrder: number | null; title: string }[];
  /** The tenant's sellers. Asked once, when the dialog opens; the dialog works without them. */
  loadSellers: () => Promise<readonly Seller[]>;
  /** Sends the lot. The caller makes the API call, reports the outcome on the console, and loads the lot when asked. */
  submit: (body: NewLotBody, loadNow: boolean) => Promise<SubmitResult>;
}

export interface LotDialog {
  close: () => void;
}

const PRICING_CHOICES: ReadonlyArray<readonly [string, string]> = [
  ['PER_LOT', 'One price for the lot'],
  ['PER_HEAD', 'Price per head'],
  ['CHOICE', "Buyer's choice"],
];

const OFFICE_NONE = 'Office (no seller)';
const RESULTS_SHOWN = 6;

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** The key that confirms or cancels an IME composition (Enter, Escape) is not a key for the form. */
const composing = (e: KeyboardEvent): boolean => e.isComposing || e.keyCode === 229;

/**
 * What a refused createLot says on the dialog's error line. The API answers a
 * taken number with 409; anything else is put plainly, with the status, so the
 * clerk can read it to the office over the phone.
 *
 * A 409 never says "try the next one": the number may be held by the very lot
 * this clerk just added (a reply lost on a slow link), and the dialog, which
 * can see the refreshed catalog, is the one that knows what to suggest.
 */
export function addLotFailure(status: number, body: unknown, words: LotWords): string {
  if (status === 409) return `${capitalize(words.low)} number is already taken in this sale. Check the catalog before choosing another.`;
  if (status === 401) return 'Your sign-in has expired. Sign in again, then add the lot.';
  if (status === 403) return 'This sign-in cannot add lots. Ask the office.';
  // The API's own sentence when it sent one ("This sale does not take "cattle"
  // lots. It takes: equine."), else its error code, else the bare status.
  const fields = body !== null && typeof body === 'object' ? (body as { message?: unknown; error?: unknown }) : null;
  const detail = [fields?.message, fields?.error, fields ? undefined : body].find((v) => typeof v === 'string' && v !== '');
  const said = typeof detail === 'string' ? `Could not add it (${status}): ${detail}` : `Could not add it (error ${status}).`;
  // A server error can land after the write: never invite a blind second add.
  return status >= 500 ? `${said.replace(/\.?$/, '')}. It may still have been added: check the catalog before adding again.` : said;
}

let openDialog: { root: HTMLElement; focus: () => void } | null = null;

/**
 * Opens the dialog over the console. Only one at a time: asking again while it
 * is open brings the open one back to the name box instead of stacking a second.
 */
export function openLotDialog(deps: LotDialogDeps): LotDialog {
  if (openDialog && openDialog.root.isConnected) {
    openDialog.focus();
    return { close: () => undefined };
  }

  const { words } = deps;
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let busy = false;
  let closed = false;
  let sellers: readonly Seller[] | 'loading' | 'failed' = 'loading';
  let chosen: Seller | null = null;

  // ---- fields ---------------------------------------------------------------
  const field = (id: string, label: string, input: HTMLElement, wrapClass = ''): HTMLElement =>
    el('div', { class: wrapClass }, el('label', { class: 'resetlbl', attrs: { for: id }, text: label }), input);

  const numInput = el('input', { class: 'resetin', attrs: { id: 'ld-num', type: 'text', autocomplete: 'off', spellcheck: 'false', enterkeyhint: 'next' } });
  numInput.value = nextLotNumber(deps.existing());
  const titleInput = el('input', { class: 'resetin', attrs: { id: 'ld-title', type: 'text', autocomplete: 'off', enterkeyhint: 'next' } });
  const askInput = el('input', { class: 'resetin', attrs: { id: 'ld-ask', type: 'text', inputmode: 'decimal', autocomplete: 'off', enterkeyhint: 'next' } });
  const reserveInput = el('input', { class: 'resetin', attrs: { id: 'ld-reserve', type: 'text', inputmode: 'decimal', autocomplete: 'off', enterkeyhint: 'done' } });

  const kindSelect = el('select', { class: 'resetin', attrs: { id: 'ld-kind' } }, ...deps.kinds.map((k) => el('option', { text: capitalize(k), attrs: { value: k } })));
  kindSelect.value = deps.kinds.includes(deps.kind) ? deps.kind : (deps.kinds[0] ?? '');

  const pricingSelect = el('select', { class: 'resetin', attrs: { id: 'ld-pricing' } }, ...PRICING_CHOICES.map(([v, label]) => el('option', { text: label, attrs: { value: v } })));
  const qtyInput = el('input', { class: 'resetin', attrs: { id: 'ld-qty', type: 'text', inputmode: 'numeric', autocomplete: 'off', enterkeyhint: 'done' } });
  const qtyField = field('ld-qty', 'Head in the group', qtyInput);
  qtyField.hidden = true;
  pricingSelect.onchange = () => {
    qtyField.hidden = pricingSelect.value === 'PER_LOT';
    if (qtyField.hidden) qtyInput.value = '';
    else qtyInput.focus();
  };

  // ---- seller ---------------------------------------------------------------
  const sellerName = el('span', { class: 'ld-seller-name', text: OFFICE_NONE, attrs: { id: 'ld-seller-name' } });
  const changeBtn = el('button', { class: 'ghost ld-change', text: 'Choose seller', attrs: { id: 'ld-change', type: 'button', 'aria-expanded': 'false', 'aria-controls': 'ld-pick' } });
  const searchInput = el('input', { class: 'resetin', attrs: { id: 'ld-search', type: 'search', autocomplete: 'off', 'aria-label': 'Search sellers by name or number' } });
  const results = el('div', { class: 'ld-results', attrs: { role: 'group', 'aria-label': 'Matching sellers' } });
  const officeBtn = el('button', { class: 'ld-pickrow', text: OFFICE_NONE, attrs: { id: 'ld-office', type: 'button' } });
  const pick = el('div', { class: 'ld-pick', attrs: { id: 'ld-pick' } }, searchInput, results, officeBtn);
  pick.hidden = true;

  const paintSeller = (): void => {
    sellerName.textContent = chosen ? chosen.name : OFFICE_NONE;
    changeBtn.textContent = chosen ? 'Change' : 'Choose seller';
  };
  const closePicker = (): void => {
    pick.hidden = true;
    changeBtn.setAttribute('aria-expanded', 'false');
    changeBtn.focus();
  };
  const choose = (s: Seller | null): void => {
    chosen = s;
    paintSeller();
    closePicker();
  };
  const paintResults = (): void => {
    if (sellers === 'loading') return setChildren(results, el('div', { class: 'ld-hint', text: 'Loading sellers...' }));
    if (sellers === 'failed') return setChildren(results, el('div', { class: 'ld-hint', text: 'Sellers could not be loaded. The lot will be added with no seller; the office can set one.' }));
    const q = searchInput.value;
    if (!q.trim()) return setChildren(results, el('div', { class: 'ld-hint', text: 'Type part of a name, or a seller number.' }));
    const found = searchSellers(sellers, q, RESULTS_SHOWN);
    if (!found.length) return setChildren(results, el('div', { class: 'ld-hint', text: 'No seller matches.' }));
    setChildren(results, ...found.map((s) => {
      const b = el('button', { class: 'ld-pickrow', attrs: { type: 'button' } }, el('span', { text: s.name }), s.sellerNumber !== null ? el('span', { class: 'ld-pickno', text: `#${s.sellerNumber}` }) : null);
      b.onclick = () => choose(s);
      return b;
    }));
  };
  searchInput.oninput = paintResults;
  searchInput.onkeydown = (e) => {
    if (e.key !== 'Enter' || composing(e)) return;
    e.preventDefault();
    if (sellers === 'loading' || sellers === 'failed') return;
    const first = searchSellers(sellers, searchInput.value, 1)[0];
    if (first) choose(first);
  };
  officeBtn.onclick = () => choose(null);
  changeBtn.onclick = () => {
    const opening = pick.hidden;
    pick.hidden = !opening;
    changeBtn.setAttribute('aria-expanded', String(opening));
    if (opening) {
      searchInput.value = '';
      paintResults();
      searchInput.focus();
    }
  };
  paintResults();
  deps.loadSellers().then(
    (list) => { sellers = list; paintResults(); },
    () => { sellers = 'failed'; paintResults(); },
  );

  // ---- actions ----------------------------------------------------------------
  const err = el('div', { class: 'reset-err', attrs: { id: 'ld-err', role: 'alert' } });
  const addBtn = el('button', { class: 'cta', text: `Add ${words.low}`, attrs: { id: 'ld-add', type: 'submit' } });
  const loadBtn = el('button', { class: 'ghost', text: 'Add and load now', attrs: { id: 'ld-load', type: 'button' } });
  const cancelBtn = el('button', { class: 'ghost', text: 'Cancel', attrs: { id: 'ld-cancel', type: 'button' } });

  const setBusy = (on: boolean): void => {
    busy = on;
    for (const b of [addBtn, loadBtn]) b.disabled = on;
    addBtn.textContent = on ? 'Adding...' : `Add ${words.low}`;
  };
  const fail = (message: string, focus?: HTMLElement): void => {
    err.textContent = message;
    focus?.focus();
  };

  // The lot that already holds a number, and whether it carries the name just
  // typed. A reply lost on a slow link leaves the clerk's own lot in the sale
  // while the dialog still says "failed": the same number AND the same name is
  // almost certainly that lot, so say so instead of offering the next number
  // (which would add the animal a second time).
  const holderOf = (number: string): { hipNumber: string; title: string } | undefined => {
    const n = number.trim().toLowerCase();
    return deps.existing().find((l) => l.hipNumber.toLowerCase() === n);
  };
  const sameName = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();
  // Numbers this dialog sent and never learned the outcome of (no answer, a
  // server error). If the sale holds one of them it is almost surely the clerk's
  // own lot even when the name was edited since (a typo fixed before a retry).
  const unconfirmed = new Set<string>();
  const isOwnLot = (holder: { hipNumber: string; title: string }, title: string): boolean =>
    sameName(holder.title, title) || unconfirmed.has(holder.hipNumber.toLowerCase());
  const probablyAdded = (holder: { hipNumber: string; title: string }): string =>
    `${capitalize(words.low)} ${holder.hipNumber} is already in the sale as "${holder.title}". If that is the ${words.low} you just added, it is done: press Cancel. If not, change the number.`;

  const send = async (loadNow: boolean): Promise<void> => {
    if (busy) return;
    err.textContent = '';
    if (!numInput.value.trim()) return fail(`Enter the ${words.low} number`, numInput);
    if (!titleInput.value.trim()) return fail('Enter the name', titleInput);
    const already = holderOf(numInput.value);
    if (already && isOwnLot(already, titleInput.value)) return fail(probablyAdded(already), numInput);
    const ask = parseDollars(askInput.value);
    if (!ask.ok) return fail('Opening ask: enter dollars, like 2500', askInput);
    const reserve = parseDollars(reserveInput.value);
    if (!reserve.ok) return fail('Reserve: enter dollars, like 2500', reserveInput);
    const grouped = pricingSelect.value !== 'PER_LOT';
    if (grouped && !/^\d+$/.test(qtyInput.value.trim())) return fail('How many head are in the group? Enter the count.', qtyInput);
    const checked = validateNewLot(
      {
        hipNumber: numInput.value,
        title: titleInput.value,
        kind: kindSelect.value,
        ...(ask.value !== undefined ? { openingAskDollars: ask.value } : {}),
        ...(reserve.value !== undefined ? { reserveDollars: reserve.value } : {}),
        pricingMode: pricingSelect.value,
        ...(grouped ? { quantity: Number(qtyInput.value.trim()) } : {}),
        ...(chosen ? { consignorId: chosen.id } : {}),
      },
      deps.existing(),
      deps.kinds,
      words,
    );
    if (!checked.ok) return fail(checked.error, /number|already exists/.test(checked.error) ? numInput : undefined);

    setBusy(true);
    let result: SubmitResult;
    try {
      result = await deps.submit(checked.body, loadNow);
    } catch (e) {
      result = { ok: false, error: e instanceof Error ? e.message : 'Could not add it.', unconfirmed: true };
    }
    if (closed) return;
    setBusy(false);
    if (result.ok) return close();
    if (result.unconfirmed) unconfirmed.add(checked.body.hipNumber.trim().toLowerCase());
    const back = loadNow ? loadBtn : addBtn; // the button that was disabled: keep focus on a live control
    const holder = holderOf(checked.body.hipNumber);
    if (holder) {
      // The caller has refreshed the catalog by now. Same name, or a number this
      // dialog could not confirm: that is our own lot (see holderOf). Another
      // name: another clerk took the number while the dialog was open, so offer
      // the next free one rather than leaving the clerk to work out what is taken.
      if (isOwnLot(holder, checked.body.title)) return fail(probablyAdded(holder), back);
      const was = checked.body.hipNumber;
      numInput.value = nextLotNumber(deps.existing());
      return fail(`${capitalize(words.low)} ${was} was just taken. The next free number, ${numInput.value}, is filled in. Add again.`, back);
    }
    fail(result.error, back);
  };

  // ---- the sheet ----------------------------------------------------------------
  // tabindex -1 on both containers: a click on a bare part of the sheet (the
  // heading, a caption, the backdrop) then puts focus on the sheet itself,
  // inside root, instead of dropping it to the page body behind.
  const form = el('form', { class: 'sheetcard ld-card', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'ld-h', novalidate: '', tabindex: '-1' } },
    el('div', { class: 'sheeth', text: `Add ${words.low} at the ring`, attrs: { id: 'ld-h' } }),
    el('div', { class: 'ld-row ld-row-num' }, field('ld-num', `${capitalize(words.low)} number`, numInput), field('ld-title', 'Name', titleInput)),
    el('div', { class: 'ld-row' }, field('ld-ask', 'Opening ask ($)', askInput), field('ld-reserve', 'Reserve ($)', reserveInput)),
    field('ld-kind', 'Kind', kindSelect),
    field('ld-pricing', 'Sold as', pricingSelect),
    qtyField,
    el('div', {}, el('div', { class: 'resetlbl', text: 'Seller' }), el('div', { class: 'ld-seller' }, sellerName, changeBtn), pick),
    err,
    el('div', { class: 'sheetact' }, addBtn, loadBtn, cancelBtn),
  );
  const root = el('div', { class: 'scrim', attrs: { id: 'ld-scrim', tabindex: '-1' } }, form);

  /** Last control that held focus, so a stray keystroke can put the clerk back where they were. */
  let lastFocus: HTMLElement | null = null;
  root.addEventListener('focusin', (e) => {
    if (e.target instanceof HTMLElement && e.target !== root && e.target !== form) lastFocus = e.target;
  });

  const controls = (): HTMLElement[] =>
    Array.from(form.querySelectorAll<HTMLElement>('input, select, button')).filter((x) => !(x as HTMLInputElement).disabled && !x.closest('[hidden]'));

  /** Escape backs out one level: an open seller picker first, then the whole sheet (whatever was typed is lost). */
  const backOut = (): void => {
    if (!pick.hidden) closePicker();
    else close();
  };

  /**
   * While the sheet is open, a key pressed with focus anywhere else (a click on
   * a part of the page that takes no focus, a button that disabled itself
   * mid-request) must not reach the console's paper-clerk keyboard strip: a bare
   * Enter there is HAMMER and Space is fair warning, on the lot being sold. This
   * runs first (capture) and stops the event, then pulls focus back in.
   */
  const keepKeysHere = (e: KeyboardEvent): void => {
    if (!root.isConnected) { // the sheet was torn out of the page without close(): never keep eating the console's keys
      closed = true;
      document.removeEventListener('keydown', keepKeysHere, true);
      return;
    }
    if (e.target instanceof Node && root.contains(e.target)) return; // the root listener below owns these
    e.stopPropagation();
    if (e.key === 'Escape') { e.preventDefault(); backOut(); return; }
    if (e.key === 'Tab') {
      e.preventDefault();
      const items = controls();
      (e.shiftKey ? items[items.length - 1] : items[0])?.focus();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return; // browser shortcuts (reload, copy) still work
    e.preventDefault();
    (lastFocus?.isConnected ? lastFocus : titleInput).focus();
  };

  function close(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', keepKeysHere, true);
    root.remove();
    if (openDialog?.root === root) openDialog = null;
    if (opener?.isConnected) opener.focus();
  }

  form.onsubmit = (e) => { e.preventDefault(); void send(false); };
  loadBtn.onclick = () => void send(true);
  cancelBtn.onclick = close;

  root.addEventListener('keydown', (e) => {
    // Nothing typed here may reach the console's paper-clerk keyboard strip
    // (hammer, fair warning, floor bid): it listens on the document and only
    // skips text fields, so a select or a button with focus would feed it.
    e.stopPropagation();
    if (composing(e)) return; // the Enter or Escape that settles an IME composition belongs to the IME
    if (e.key === 'Escape') { e.preventDefault(); backOut(); return; }
    const t = e.target as HTMLElement;
    if (e.key === 'Enter' && t instanceof HTMLInputElement && t !== searchInput) {
      e.preventDefault();
      // Enter in a box never adds the lot. The phone keyboard's "next" and "done"
      // keys are ordinary Enters, and Kind, Sold as and Seller sit below the money
      // boxes: an Enter that added would send the lot before the clerk had seen
      // them. Number, name and ask move on; reserve and head count land on the Add
      // button (which also closes a phone keyboard), and Enter there adds.
      const next = t === numInput ? titleInput : t === titleInput ? askInput : t === askInput ? reserveInput : addBtn;
      next.focus();
      return;
    }
    if (e.key !== 'Tab') return;
    // Keep Tab inside the sheet. Focus can sit on the bare sheet or backdrop
    // (a click on the heading): that is inside the sheet but in no control, so
    // the browser's own Tab would walk out to the console behind it.
    const items = controls();
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !items.includes(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
    else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  });

  document.addEventListener('keydown', keepKeysHere, true);
  document.body.append(root);
  openDialog = { root, focus: () => titleInput.focus() };
  titleInput.focus();
  return { close };
}
